"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FaCheck } from "react-icons/fa";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { submitContactForm } from "@/app/[locale]/contact/actions";

interface ContactFormProps {
  formTranslationNamespace: string;
  validationTranslationNamespace: string;
}

export default function ContactForm({
  formTranslationNamespace,
  validationTranslationNamespace,
}: ContactFormProps) {
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const formT = useTranslations(formTranslationNamespace);
  const validationT = useTranslations(validationTranslationNamespace);

  // Prevent hydration errors by only rendering on client
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Define schema inside the component
  const contactFormSchema = z.object({
    name: z.string().min(2, validationT("required")),
    email: z.string().email(validationT("invalid")),
    subject: z.string().min(2, formT("subjectError")),
    message: z.string().min(10, validationT("min")),
  });

  type ContactFormValues = z.infer<typeof contactFormSchema>;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    try {
      // Send form data to server action
      const result = await submitContactForm(data);

      if (result.status === "success") {
        // Show success message and reset form
        setIsSubmitSuccessful(true);
        form.reset();

        // Hide success message after 5 seconds
        setTimeout(() => {
          setIsSubmitSuccessful(false);
        }, 5000);
      } else {
        // Handle error
        console.error("Error submitting form:", result.message);
        // Here you could set an error state and show it to the user
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Here you would handle the error
    }
  };

  // Show a minimal placeholder during SSR/hydration
  if (!isMounted) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-pulse h-6 w-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div>
      {isSubmitSuccessful ? (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaCheck className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{formT("success")}</p>
            </div>
          </div>
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  {formT("name")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  {formT("email")}
                </FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  {formT("subject")}
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">
                  {formT("message")}
                </FormLabel>
                <FormControl>
                  <Textarea rows={4} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {form.formState.isSubmitting ? formT("sending") : formT("submit")}
          </Button>
        </form>
      </Form>
    </div>
  );
}
